import React, { useContext, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import AppError from "@/AppError";
import Dialog from "@/components/Dialog";
import Dropzone from "@/components/Form/Dropzone";
import utils from "@/utils/common";
import {
  BMW_ORDER,
  ERROR,
  ScanStatus_Clean,
  ScanStatus_Malicious,
  SUCCESS,
  userNameKey,
  userQnumberKey,
} from "@/constants/constants";
import { AppContext } from "@/context/AppContext";
import { handleApiError } from "@/utils/handleApiError";
import { StyledError } from "@/components/Styles/PagesComponentsCommon";
import { styled } from "@mui/material";
import { useOrderVerificationMutation } from "@/services/mutations";
import { useTranslation } from "react-i18next";
import apiOrdersData from "@/services/apiOrders";
import { useDocumentScanLinkEntityById} from "@/services/queries";
import apiUIData from "@/services/apiUI";

const VerificationDialog = ({
  id,
  setIsLoading,
  open,
  instructions,
  setOpen,
  setShowAlert,
  setUpdateTimeline,
}) => {
  const { setAlertStatus, setAlertMessage, userData: { [userQnumberKey]: userQNumber },} = useContext(AppContext);
  
  const [base64File, setBase64File] = useState(null);
  const [errorTitle, setErrorTitle] = useState(false);
  const [errorFile, setErrorFile] = useState(false);
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const [apiError, setApiError] = useState("");
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);    
  const [enableScanPolling, setEnableScanPolling] = useState(false);
    
  const { t } = useTranslation();
  const acceptedFiles = ["application/pdf"];
  const errorMessage = t("orderDetails.verificationDialog.errorMessage");
  const filesLimit = 1;
  const [docScanLinkEntityId, setDocScanLinkEntityId] = useState(null);
  const [timeoutLimit, setTimeoutLimit] = useState(null);

  const uploadVerificationMutation = useOrderVerificationMutation();

  const scanQuery = useDocumentScanLinkEntityById(
      docScanLinkEntityId,
      {
        enabled: enableScanPolling,
        refetchIntervalMs: timeoutLimit,
        maxAttempts: 5,
      }
    );

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    apiUIData
      .getCrmConfiguration("NKAM.DealerPortal.Note.Timeout", controller)
      .then((response) => {
        const timeoutLimit = response?.data?.result?.integerValue ?? 5000;
        setTimeoutLimit(timeoutLimit);
      })
      .catch((error) => {
        setApiError(handleApiError(error));
        setTimeoutLimit(5000);
      });

    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    // Clears the error when file is added.
    if (files.length > 0) {
      setErrorFile(false);
    }
  }, [files]);

  useEffect(() => {
    setIsLoading(uploadVerificationMutation.isPending);
    if (uploadVerificationMutation.isError) {
      setApiError(handleApiError(uploadVerificationMutation.error));
    }
    if (uploadVerificationMutation.isSuccess) {
      handleClose();
      setShowAlert(true);
      setAlertStatus(SUCCESS);
      setAlertMessage(t("orderDetails.verificationDialog.successAlert"));
      setUpdateTimeline(true);
    }
  }, [uploadVerificationMutation.status])


useEffect(() => {
  if (!enableScanPolling) return;

  const timeoutId = setTimeout(() => {
    setEnableScanPolling(false);
    setIsLoading(false);
    setApiError({});
    setMaxAttemptsReached(true);
    handleClose();
    setShowAlert(true);
    setAlertStatus(ERROR);
    setAlertMessage(t("orderDetails.verificationDialog.errorTimeoutMessage"));
     
  }, 5 * timeoutLimit);

  return () => clearTimeout(timeoutId);
}, [enableScanPolling]);

  
useEffect(() => {
  if (!scanQuery.data) return;

  const isScanCompleted = scanQuery.data?.data?.result?.isScanCompleted;

  if (isScanCompleted && !maxAttemptsReached) {

    setEnableScanPolling(false);

    const scanResultValue = scanQuery.data?.data?.result?.scanResultValue;

    if (scanResultValue === ScanStatus_Clean) {
    
      uploadVerificationMutation.mutate([
                          id,
                          title,
                          files.length > 0 ? files[0]?.name : null,
                          base64File ? base64File?.substr(base64File.indexOf(",") + 1) : null,
                        ]);
        
    } 
    else if (scanResultValue === ScanStatus_Malicious) {
        
        const uploadedFileName = files.length > 0 ? files[0]?.name : null;
        
        apiOrdersData
        .createOrderTimelineNote({
          title : t("timelineDialog.maliciousFileSubject"),
          attachmentOrComments: t("timelineDialog.appendedComment", {
            comment : t("timelineDialog.maliciousFileComment") + uploadedFileName,
            userName: utils.secureGetFromLocalStorage(userNameKey),
            cNumber: userQNumber,
          }),
          documentBody: null,
          fileName: null,
          mimeType: null,
          regarding: {
            id,
            logicalName: BMW_ORDER,
          },
        })
        .then(() => {
          setIsLoading(false);       
          setApiError({});
          handleClose();
          setShowAlert(true);
          setAlertStatus(ERROR);
          setAlertMessage(t("orderDetails.verificationDialog.errorMaliciousFileMessage"));
          setUpdateTimeline(true);          
        })
        .catch((err) => {
          setIsLoading(false);
          setApiError(handleApiError(err));
        });  
    }
    else {
      setIsLoading(false);
      setApiError(handleApiError({ message: t("orderDetails.verificationDialog.errorInvalidScanResult")}));
    }
  }
}, [scanQuery.data]);

  useEffect(() => {
    // Clears the error when title is updated.
    if (title) {
      setErrorTitle(false);
    }
  }, [title]);

  const clearForm = () => {
    setErrorFile(false);
    setErrorTitle(false);
    setTitle("");
  };

  const handleClose = () => {
    setOpen(false);
    clearForm();
  };

  const validateForm = () => {
    if (files.length === 0 && title === "") {
      setErrorFile(true);
      setErrorTitle(true);
    } else if (title === "") {
      setErrorFile(false);
      setErrorTitle(true);
    } else if (files.length === 0) {
      setErrorFile(true);
      setErrorTitle(false);
    } else {
      setErrorFile(false);
      setErrorTitle(false);
    }
  };

  const uploadVerification = async () => {
    validateForm();

    if (files.length > 0 && title) {

      setIsLoading(true);

      const createDocScanLinkData = {
              callingEntityName: BMW_ORDER,
              callingEntityId: id,
              fileName: files.length > 0 ? files[0]?.name : null,
              mimeType: files.length > 0 ? files[0]?.type : null,
            };

      await apiOrdersData.createDocScanLinkForOrder(createDocScanLinkData)
      .then((responseCreateDocScanLink) => {
          
          if (responseCreateDocScanLink?.statusCode === 200 && responseCreateDocScanLink?.data?.result)
          {
              setDocScanLinkEntityId(responseCreateDocScanLink.data.result);

              const uploadData = {
                  entityId: id,
                  docScanLinkEntityId: responseCreateDocScanLink.data.result,
                  fileName: files.length > 0 ? files[0].name : null,
                  mimeType: files.length > 0 ? files[0].type : null,
                  base64File: base64File
                    ? base64File?.substr(base64File.indexOf(",") + 1)
                    : null,
                };

                apiOrdersData.uploadDocForScanningForOrder(uploadData)
                .then((responseUpload) => {
                  
                  if(responseUpload?.statusCode === 200)
                  { 
                      setEnableScanPolling(true);
                  }
                })
                .catch((error) => {
                  setApiError(handleApiError(error));
                });
          }
          else 
          {
            setApiError(handleApiError({ message: "Scan link creation failed or GUID does not exist for Order."}));
          }
        })
        .catch((error) => {
          setApiError(handleApiError(error));
        });
    }
  };

  return (
    <>
      <Dialog
        confirmationButtonText={t("orderDetails.verificationDialog.dialog.confirmationButtonText")}
        handleClose={handleClose}
        handler={uploadVerification}
        id="uploadVerification"
        open={open}
        prompt={t("orderDetails.verificationDialog.dialog.prompt")}
        title={t("orderDetails.verificationDialog.dialog.title")}
      >
        <StyledTextField
          label={t("orderDetails.verificationDialog.verificationDialogFields.title")}
          error={!!errorTitle}
          required={true}
          value={title}
          helperText={errorTitle ? errorMessage : null}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />

        <StyledInscructionsBox>
          <ReactMarkdown>{instructions}</ReactMarkdown>
        </StyledInscructionsBox>

        <Dropzone
          acceptedFiles={acceptedFiles}
          files={files}
          filesLimit={filesLimit}
          setBase64File={setBase64File}
          setFiles={setFiles}
        />

        {errorFile ? (
          <StyledError component="p" variant="caption">
            {errorMessage}
          </StyledError>
        ) : null}
      </Dialog>

      {apiError?.isError && (
        <AppError apiError={apiError} setApiError={setApiError} />
      )}
    </>
  );
};

const StyledTextField = styled(TextField)(() => ({
  margin: 0,
}));

const StyledInscructionsBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1),
  margin: theme.spacing(3, 0),
}));

export default VerificationDialog;
